import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiDownload, HiMoon, HiSun } from 'react-icons/hi';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getCupScoresLocale,
  getCupScoresRoleLabels,
  scheduleSectionTitle,
  standingsSubtitle,
  thirdsSubtitle,
} from '../data/cupScoresLocale';
import JuniorCupLangToggle from './JuniorCupLangToggle';
import {
  GENERATIONS,
  physicalPitch,
  computeKnockoutBracket,
  applyKoBracketOverride,
  applyKnockoutAdvancement,
  knockoutFinalMedalists,
  type KnockoutMedalists,
  thirdPlacedTeams,
  thirdPlacedTeamsProvisional,
  isGroupStageComplete,
  getGenerationFormat,
  getGenerationSchedule,
  groupsForGeneration,
  playoffWinner,
  playoffSchematic,
  displayTeamName,
  formatPairLabel,
  formatSchematicPair,
  resolveSchematicTeam,
  type SchematicResolveContext,
  teamStorageKey,
  QF_SCHEMATIC,
  type GenerationYear,
  type GenerationSchedule,
  type GroupId,
  type ScorePair,
  type TeamNamesMap,
  type GroupMatchDef,
  type KoMatchDef,
  type KnockoutBracket,
  type KnockoutFormat,
  type CupKoBracketOverridesRecord,
  type BracketPair,
  buildStandings,
  slotEndLabel,
  slotKickoffLabel,
  medalCeremonyStartClock,
  medalCeremonyApproxEndClock2014,
} from '../data/cupScoresData';
import {
  CUP_SCORES_SYNC_EVENT,
  CUP_LIVE_POLL_MS,
  fetchCupLiveData,
  loadCupScoresFromCache,
  saveCupLiveData,
} from '../services/cupScoresStore';
import { exportCupSchedulePdf } from '../utils/cupSchedulePdfExport';
import './CupScores.css';

const THEME_STORAGE_KEY = 'juniorCupScores.theme';

const EMPTY_SCHEDULE: GenerationSchedule = {
  groupMatches: [],
  koMatches: [],
  groupLastSlot: 0,
  koLastSlot: 0,
  slotKickoffOffsets: [0],
};

type ResultsMap = Record<string, ScorePair>;
type CupTheme = 'dark' | 'light';

function readStoredTheme(): CupTheme {
  try {
    const s = localStorage.getItem(THEME_STORAGE_KEY);
    if (s === 'light' || s === 'dark') return s;
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

const QF_INDEX: Record<'qf1' | 'qf2' | 'qf3' | 'qf4', number> = {
  qf1: 0,
  qf2: 1,
  qf3: 2,
  qf4: 3,
};

const PO_INDEX: Record<'po1' | 'po2', number> = { po1: 0, po2: 1 };

/** Knock-out slot letters: E,F for play-offs / first two QFs; G,H for second two QFs. */
const KO_SLOT_LETTERS = ['E', 'F', 'G', 'H'] as const;

const EMPTY_BRACKET_PAIR: BracketPair = { home: '', away: '' };

function emptyQuarterOverride(): { format: 'quarter_finals'; qf: BracketPair[] } {
  return {
    format: 'quarter_finals',
    qf: [
      { ...EMPTY_BRACKET_PAIR },
      { ...EMPTY_BRACKET_PAIR },
      { ...EMPTY_BRACKET_PAIR },
      { ...EMPTY_BRACKET_PAIR },
    ],
  };
}

function emptySemiDirectOverride(): {
  format: 'semi_direct';
  playoffs: [BracketPair, BracketPair];
  sf: [BracketPair, BracketPair];
} {
  return {
    format: 'semi_direct',
    playoffs: [{ ...EMPTY_BRACKET_PAIR }, { ...EMPTY_BRACKET_PAIR }],
    sf: [{ ...EMPTY_BRACKET_PAIR }, { ...EMPTY_BRACKET_PAIR }],
  };
}

function quarterDraftForGen(
  draft: CupKoBracketOverridesRecord,
  gen: GenerationYear
): { format: 'quarter_finals'; qf: BracketPair[] } {
  const raw = draft[String(gen)];
  if (raw?.format === 'quarter_finals' && Array.isArray(raw.qf) && raw.qf.length === 4) {
    return {
      format: 'quarter_finals',
      qf: raw.qf.map((p) => ({
        home: String(p?.home ?? ''),
        away: String(p?.away ?? ''),
      })),
    };
  }
  return emptyQuarterOverride();
}

function semiDraftForGen(
  draft: CupKoBracketOverridesRecord,
  gen: GenerationYear
): { format: 'semi_direct'; playoffs: [BracketPair, BracketPair]; sf: [BracketPair, BracketPair] } {
  const raw = draft[String(gen)];
  if (raw?.format === 'semi_direct' && raw.playoffs && raw.sf) {
    return {
      format: 'semi_direct',
      playoffs: [
        { home: String(raw.playoffs[0]?.home ?? ''), away: String(raw.playoffs[0]?.away ?? '') },
        { home: String(raw.playoffs[1]?.home ?? ''), away: String(raw.playoffs[1]?.away ?? '') },
      ],
      sf: [
        { home: String(raw.sf[0]?.home ?? ''), away: String(raw.sf[0]?.away ?? '') },
        { home: String(raw.sf[1]?.home ?? ''), away: String(raw.sf[1]?.away ?? '') },
      ],
    };
  }
  return emptySemiDirectOverride();
}

function koRowLabel(
  labelKey: KoMatchDef['labelKey'],
  koBracket: KnockoutBracket | null,
  L: ReturnType<typeof getCupScoresLocale>,
  gen: GenerationYear,
  results: ResultsMap,
  teamNames: TeamNamesMap,
  schematicCtx: SchematicResolveContext,
  poScheme: ReturnType<typeof playoffSchematic>
): string {
  const pair = (homeId: string, awayId: string) =>
    formatPairLabel(homeId, awayId, teamNames, gen);
  const schematic = (h: string, a: string, vs = ' vs ') =>
    formatSchematicPair(h, a, schematicCtx, vs);

  if (labelKey === 'po1' || labelKey === 'po2') {
    const letter = KO_SLOT_LETTERS[PO_INDEX[labelKey]];
    if (koBracket?.format === 'semi_direct') {
      const p = koBracket.playoffs[PO_INDEX[labelKey]];
      return `${letter} · ${pair(p.home, p.away)}`;
    }
    const [h, a] = poScheme[labelKey];
    return `${letter} · ${schematic(h, a)}`;
  }
  if (labelKey === 'qf1' || labelKey === 'qf2' || labelKey === 'qf3' || labelKey === 'qf4') {
    const letter = KO_SLOT_LETTERS[QF_INDEX[labelKey]];
    if (koBracket?.format === 'quarter_finals') {
      const p = koBracket.qf[QF_INDEX[labelKey]];
      return `${letter} · ${pair(p.home, p.away)}`;
    }
    const [h, a] = QF_SCHEMATIC[labelKey];
    return `${letter} · ${schematic(h, a)}`;
  }
  if (labelKey === 'sf1') {
    if (getGenerationFormat(gen).knockout === 'semi_direct') {
      const w = playoffWinner(results, gen, 'po1', koBracket);
      if (w && koBracket?.format === 'semi_direct') {
        return `${L.koLabels.sf1} · ${pair(koBracket.sf[0].home, w)}`;
      }
      const po1 =
        koBracket?.format === 'semi_direct'
          ? pair(koBracket.playoffs[0].home, koBracket.playoffs[0].away)
          : schematic(poScheme.po1[0], poScheme.po1[1], '–');
      const b1 = resolveSchematicTeam('B1', schematicCtx);
      return `${L.koLabels.sf1} · ${b1} vs ${schematicCtx.labels.rolePlayoffWinner('E')} (${po1})`;
    }
    return L.bracketSemi1Pair;
  }
  if (labelKey === 'sf2') {
    if (getGenerationFormat(gen).knockout === 'semi_direct') {
      const w = playoffWinner(results, gen, 'po2', koBracket);
      if (w && koBracket?.format === 'semi_direct') {
        return `${L.koLabels.sf2} · ${pair(koBracket.sf[1].home, w)}`;
      }
      const po2 =
        koBracket?.format === 'semi_direct'
          ? pair(koBracket.playoffs[1].home, koBracket.playoffs[1].away)
          : schematic(poScheme.po2[0], poScheme.po2[1], '–');
      const a1 = resolveSchematicTeam('A1', schematicCtx);
      return `${L.koLabels.sf2} · ${a1} vs ${schematicCtx.labels.rolePlayoffWinner('F')} (${po2})`;
    }
    return L.bracketSemi2Pair;
  }
  if (labelKey === 'final') {
    if (koBracket?.format === 'league_playoffs') {
      const p = koBracket.final;
      return `${L.koLabels.final} · ${pair(p.home, p.away)}`;
    }
    if (
      koBracket &&
      (koBracket.format === 'quarter_finals' || koBracket.format === 'semi_direct') &&
      koBracket.final.home &&
      koBracket.final.away
    ) {
      return `${L.koLabels.final} · ${pair(koBracket.final.home, koBracket.final.away)}`;
    }
    return L.koLabels.final;
  }
  if (labelKey === 'third') {
    if (koBracket?.format === 'league_playoffs') {
      const p = koBracket.thirdPlaceMatch;
      return `${L.koLabels.third} · ${pair(p.home, p.away)}`;
    }
    if (
      koBracket &&
      (koBracket.format === 'quarter_finals' || koBracket.format === 'semi_direct') &&
      koBracket.thirdPlaceMatch.home &&
      koBracket.thirdPlaceMatch.away
    ) {
      return `${L.koLabels.third} · ${pair(koBracket.thirdPlaceMatch.home, koBracket.thirdPlaceMatch.away)}`;
    }
    return L.koLabels.third;
  }
  return L.koLabels[labelKey];
}

function collectBracketTeamIds(bracket: KnockoutBracket): string[] {
  const ids: string[] = [];
  const push = (p: BracketPair) => {
    if (p.home) ids.push(p.home);
    if (p.away) ids.push(p.away);
  };
  if (bracket.format === 'quarter_finals') {
    bracket.qf.forEach(push);
    bracket.sf.forEach(push);
    push(bracket.final);
    push(bracket.thirdPlaceMatch);
  } else if (bracket.format === 'semi_direct') {
    bracket.playoffs.forEach(push);
    bracket.sf.forEach(push);
    push(bracket.final);
    push(bracket.thirdPlaceMatch);
  } else if (bracket.format === 'league_playoffs') {
    push(bracket.final);
    push(bracket.thirdPlaceMatch);
  }
  return ids;
}

function getKoMatchPair(k: KoMatchDef, bracket: KnockoutBracket | null): BracketPair | null {
  if (!bracket) return null;
  const key = k.labelKey;
  if (bracket.format === 'quarter_finals') {
    if (key === 'qf1' || key === 'qf2' || key === 'qf3' || key === 'qf4') {
      return bracket.qf[QF_INDEX[key]];
    }
  }
  if (bracket.format === 'semi_direct') {
    if (key === 'po1' || key === 'po2') return bracket.playoffs[PO_INDEX[key]];
    if (key === 'sf1') return bracket.sf[0];
    if (key === 'sf2') return bracket.sf[1];
    if (key === 'final') return bracket.final;
    if (key === 'third') return bracket.thirdPlaceMatch;
  }
  if (bracket.format === 'quarter_finals') {
    if (key === 'sf1') return bracket.sf[0];
    if (key === 'sf2') return bracket.sf[1];
    if (key === 'final') return bracket.final;
    if (key === 'third') return bracket.thirdPlaceMatch;
  }
  if (bracket.format === 'league_playoffs') {
    if (key === 'final') return bracket.final;
    if (key === 'third') return bracket.thirdPlaceMatch;
  }
  return null;
}

function matchInvolvesTeam(home: string, away: string, teamId: string): boolean {
  return home === teamId || away === teamId;
}

export type CupScoresProps = {
  /** When false (public #cup-scores), score fields are disabled. */
  editable?: boolean;
  onAdminLogout?: () => void;
};

const CupScores: React.FC<CupScoresProps> = ({ editable = false, onAdminLogout }) => {
  const { language } = useLanguage();
  const L = useMemo(() => getCupScoresLocale(language), [language]);
  const editableRef = React.useRef(editable);
  editableRef.current = editable;
  const [gen, setGen] = useState<GenerationYear>(2014);
  const cached = useMemo(() => loadCupScoresFromCache(), []);
  const [results, setResults] = useState<ResultsMap>(() => cached.results);
  const [teamNames, setTeamNames] = useState<TeamNamesMap>(() => cached.teamNames);
  const [koBracketDraft, setKoBracketDraft] = useState<CupKoBracketOverridesRecord>(() => ({
    ...(cached.koBracketOverrides ?? {}),
  }));
  const [theme, setTheme] = useState<CupTheme>(() => readStoredTheme());
  const [teamFilterQuery, setTeamFilterQuery] = useState('');
  const [teamFilterId, setTeamFilterId] = useState<string | null>(null);
  const hydratedRef = React.useRef(false);
  const resultsRef = React.useRef(results);
  const teamNamesRef = React.useRef(teamNames);
  const koBracketDraftRef = React.useRef(koBracketDraft);
  resultsRef.current = results;
  teamNamesRef.current = teamNames;
  koBracketDraftRef.current = koBracketDraft;

  const format = useMemo(() => getGenerationFormat(gen), [gen]);
  const poScheme = useMemo(() => playoffSchematic(gen), [gen]);
  const [schedule, setSchedule] = useState<GenerationSchedule | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSchedule(null);
    const id = window.setTimeout(() => {
      try {
        const s = getGenerationSchedule(gen);
        if (!cancelled) setSchedule(s);
      } catch {
        if (!cancelled) setSchedule(EMPTY_SCHEDULE);
      }
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [gen]);

  const sched = schedule ?? EMPTY_SCHEDULE;

  const activeGroups = useMemo(() => groupsForGeneration(gen), [gen]);
  const computedBracket = useMemo(() => computeKnockoutBracket(results, gen), [results, gen]);
  const effectiveBracket = useMemo(() => {
    const merged = applyKoBracketOverride(computedBracket, gen, koBracketDraft[String(gen)]);
    return applyKnockoutAdvancement(merged, results, gen);
  }, [computedBracket, gen, koBracketDraft, results]);
  const po1Winner = useMemo(() => playoffWinner(results, gen, 'po1', effectiveBracket), [results, gen, effectiveBracket]);
  const po2Winner = useMemo(() => playoffWinner(results, gen, 'po2', effectiveBracket), [results, gen, effectiveBracket]);
  const koMedalists = useMemo(
    () => knockoutFinalMedalists(results, gen, effectiveBracket),
    [results, gen, effectiveBracket]
  );
  const thirdsList = useMemo(() => {
    if (format.knockout !== 'quarter_finals') return [];
    if (isGroupStageComplete(results, gen)) {
      return thirdPlacedTeams(results, gen);
    }
    return thirdPlacedTeamsProvisional(results, gen);
  }, [results, gen, format.knockout]);
  const groupStageComplete = useMemo(
    () => isGroupStageComplete(results, gen),
    [results, gen]
  );

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    document.title = editable ? L.documentTitleAdmin : L.documentTitle;
    return () => {
      document.title = 'FK Junior';
    };
  }, [L.documentTitle, L.documentTitleAdmin, editable]);

  const applyLiveData = useCallback((data: Awaited<ReturnType<typeof fetchCupLiveData>>) => {
    setResults(data.results);
    setTeamNames(data.teamNames);
    setKoBracketDraft(data.koBracketOverrides ?? {});
  }, []);

  /** Učitaj javne podatke sa servera; periodično osvežavanje na #cup-scores. */
  useEffect(() => {
    let cancelled = false;

    const pull = () =>
      fetchCupLiveData().then((data) => {
        if (cancelled) return;
        applyLiveData(data);
      });

    pull().finally(() => {
      if (!cancelled) hydratedRef.current = true;
    });

    const poll = window.setInterval(() => {
      if (!editableRef.current) pull();
    }, CUP_LIVE_POLL_MS);

    const onSync = () => {
      if (!editableRef.current) pull();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !editableRef.current) pull();
    };

    window.addEventListener(CUP_SCORES_SYNC_EVENT, onSync);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.removeEventListener(CUP_SCORES_SYNC_EVENT, onSync);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [applyLiveData]);

  /** Admin: snimi na server posle izmene (debounce). */
  useEffect(() => {
    if (!editable || !hydratedRef.current) return;
    const timer = window.setTimeout(() => {
      void saveCupLiveData({
        results: resultsRef.current,
        teamNames: teamNamesRef.current,
        koBracketOverrides: koBracketDraftRef.current,
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [results, teamNames, koBracketDraft, editable]);

  const formatTeam = useCallback(
    (teamId: string) => displayTeamName(teamId, teamNames, gen),
    [teamNames, gen]
  );

  const formatPairVs = useCallback(
    (homeId: string, awayId: string) => formatPairLabel(homeId, awayId, teamNames, gen, ' v '),
    [teamNames, gen]
  );

  const roleLabels = useMemo(() => getCupScoresRoleLabels(language), [language]);
  const schematicCtx = useMemo(
    (): SchematicResolveContext => ({
      results,
      names: teamNames,
      generation: gen,
      labels: roleLabels,
    }),
    [results, teamNames, gen, roleLabels]
  );

  const resultKey = useCallback((matchId: string) => `${gen}-${matchId}`, [gen]);

  const commitTeamName = useCallback(
    (teamId: string, value: string) => {
      if (!editable) return;
      const key = teamStorageKey(gen, teamId);
      const trimmed = value.trim();
      setTeamNames((prev) => {
        if (!trimmed) {
          const { [key]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [key]: trimmed };
      });
    },
    [gen, editable]
  );

  const commitScore = useCallback(
    (matchId: string, side: 'home' | 'away', value: string) => {
      if (!editable) return;
      const key = `${gen}-${matchId}`;
      let parsed: number | null = null;
      if (value !== '') {
        const n = parseInt(value, 10);
        parsed = Number.isNaN(n) ? null : Math.max(0, Math.min(99, n));
      }
      setResults((prev) => {
        const cur = prev[key] ?? { home: null, away: null };
        const updated = { ...cur, [side]: parsed };
        if (updated.home === null && updated.away === null && updated.penHome == null && updated.penAway == null) {
          const { [key]: _, ...rest } = prev;
          return rest;
        }
        if (updated.home !== updated.away) {
          delete updated.penHome;
          delete updated.penAway;
        }
        return { ...prev, [key]: updated };
      });
    },
    [gen, editable]
  );

  const commitPenScore = useCallback(
    (matchId: string, side: 'penHome' | 'penAway', value: string) => {
      if (!editable) return;
      const key = `${gen}-${matchId}`;
      let parsed: number | null = null;
      if (value !== '') {
        const n = parseInt(value, 10);
        parsed = Number.isNaN(n) ? null : Math.max(0, Math.min(99, n));
      }
      setResults((prev) => {
        const cur = prev[key] ?? { home: null, away: null };
        const updated = { ...cur, [side]: parsed };
        return { ...prev, [key]: updated };
      });
    },
    [gen, editable]
  );

  const slots = useMemo(() => {
    const rows: { slot: number; items: GroupMatchDef[]; ko: KoMatchDef[] }[] = [];
    for (let s = 0; s <= sched.koLastSlot; s += 1) {
      rows.push({
        slot: s,
        items: sched.groupMatches.filter((m) => m.startSlot === s),
        ko: sched.koMatches.filter((k) => k.startSlot === s),
      });
    }
    return rows;
  }, [sched]);

  const teamOptions = useMemo(() => {
    const ids = new Set<string>();
    sched.groupMatches.forEach((m) => {
      ids.add(m.home);
      ids.add(m.away);
    });
    if (effectiveBracket) {
      collectBracketTeamIds(effectiveBracket).forEach((id) => ids.add(id));
    }
    return Array.from(ids)
      .map((id) => ({ id, label: displayTeamName(id, teamNames, gen) }))
      .sort((a, b) => a.label.localeCompare(b.label, language === 'sr' ? 'sr' : 'en'));
  }, [sched, effectiveBracket, teamNames, gen, language]);

  const teamFilterSuggestions = useMemo(() => {
    const q = teamFilterQuery.trim().toLowerCase();
    if (!q) return teamOptions.slice(0, 14);
    return teamOptions
      .filter((o) => o.label.toLowerCase().includes(q) || o.id.toLowerCase().includes(q))
      .slice(0, 14);
  }, [teamOptions, teamFilterQuery]);

  const filteredSlots = useMemo(() => {
    if (!teamFilterId) return slots;
    return slots
      .map(({ slot, items, ko }) => ({
        slot,
        items: items.filter((m) => matchInvolvesTeam(m.home, m.away, teamFilterId)),
        ko: ko.filter((k) => {
          const pair = getKoMatchPair(k, effectiveBracket);
          return pair ? matchInvolvesTeam(pair.home, pair.away, teamFilterId) : false;
        }),
      }))
      .filter(({ items, ko }) => items.length > 0 || ko.length > 0);
  }, [slots, teamFilterId, effectiveBracket]);

  const teamFilterLabel = useMemo(() => {
    if (!teamFilterId) return null;
    return teamOptions.find((o) => o.id === teamFilterId)?.label ?? formatTeam(teamFilterId);
  }, [teamFilterId, teamOptions, formatTeam]);

  useEffect(() => {
    setTeamFilterId(null);
    setTeamFilterQuery('');
  }, [gen]);

  const selectTeamFilter = useCallback((id: string) => {
    setTeamFilterId(id);
    const label = displayTeamName(id, teamNames, gen);
    setTeamFilterQuery(label);
  }, [teamNames, gen]);

  const clearTeamFilter = useCallback(() => {
    setTeamFilterId(null);
    setTeamFilterQuery('');
  }, []);

  const selectGeneration = useCallback((y: GenerationYear) => {
    setGen(y);
    clearTeamFilter();
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }, [clearTeamFilter]);

  const handleExportPdf = useCallback(() => {
    void exportCupSchedulePdf({
      lang: language,
      generation: gen,
      teamNames,
      results,
    }).catch(() => {
      /* font/network failure — silent; user can retry */
    });
  }, [language, gen, teamNames, results]);

  const navGroups = useMemo(() => {
    const groupNav: { id: string; label: string }[] = [
      { id: 'cup-scores-anchor-schedule', label: L.navSchedule },
      { id: 'cup-scores-anchor-tables', label: L.navTables },
    ];
    const groupLabels: Record<GroupId, string> = {
      A: L.navGroupA,
      B: L.navGroupB,
      C: L.navGroupC,
    };
    for (const g of activeGroups) {
      groupNav.push({
        id: `cup-scores-anchor-group-${g.toLowerCase()}`,
        label: groupLabels[g],
      });
    }
    if (format.knockout === 'quarter_finals') {
      groupNav.push({ id: 'cup-scores-anchor-thirds', label: L.navThirds });
    }
    groupNav.push({
      id: 'cup-scores-anchor-bracket',
      label: format.knockout === 'league_table' ? L.navPlacements : L.navBracket,
    });
    return groupNav;
  }, [L, activeGroups, format.knockout]);

  if (!schedule) {
    return (
      <div className="cup-scores-page" data-theme={theme}>
        <main className="cup-scores-main">
          <p className="cup-scores-muted cup-scores-schedule-loading">{L.scheduleLoading}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="cup-scores-page" data-theme={theme}>
      <nav className="cup-scores-nav" aria-label="Cup scores">
        <div className="cup-scores-nav__top">
          <div className="cup-scores-nav__toolbar">
            <div className="cup-scores-nav__actions" role="group" aria-label={L.themeToggleGroup}>
              <button
                type="button"
                className="cup-scores-export-btn"
                onClick={handleExportPdf}
                aria-label={L.exportPdfAria}
                title={L.exportPdf}
              >
                <HiDownload aria-hidden size={18} />
                <span className="cup-scores-export-btn__label">{L.exportPdf}</span>
              </button>
              <button
                type="button"
                className="cup-scores-theme-btn"
                onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
                aria-label={theme === 'dark' ? L.themeUseLight : L.themeUseDark}
                aria-pressed={theme === 'light'}
              >
                {theme === 'dark' ? <HiSun aria-hidden size={20} /> : <HiMoon aria-hidden size={20} />}
              </button>
              <JuniorCupLangToggle />
              {editable && onAdminLogout ? (
                <button
                  type="button"
                  className="cup-scores-admin-logout"
                  onClick={onAdminLogout}
                >
                  {L.adminLogout}
                </button>
              ) : null}
            </div>
          </div>

          <header className="cup-scores-nav__hero">
            <div className="cup-scores-nav__hero-glow" aria-hidden />
            <h1 className="cup-scores-nav__hero-title">{L.pageTitle}</h1>
            <p className="cup-scores-nav__hero-lead">{L.pageSubtitle}</p>
          </header>

          <div className="cup-scores-nav__gen">
            <div className="cup-scores-tabs-wrap" role="tablist" aria-label={L.genTab}>
              {GENERATIONS.map((y) => (
                <button
                  key={y}
                  type="button"
                  role="tab"
                  aria-selected={gen === y}
                  className={`cup-scores-tab${gen === y ? ' cup-scores-tab--active' : ''}`}
                  onClick={() => selectGeneration(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div className="cup-scores-nav__jumps" role="navigation" aria-label={L.navJumpLabel}>
            {navGroups.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className="cup-scores-nav__jump"
                onClick={() => {
                  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="cup-scores-main">
        <section
          id="cup-scores-anchor-schedule"
          className="cup-scores-card cup-scores-card--schedule cup-scores-anchor-target"
          aria-labelledby="sched-h"
        >
          <div className="cup-scores-card__head">
            <h2 id="sched-h">{scheduleSectionTitle(L, format)}</h2>
          </div>
          {editable ? <p className="cup-scores-muted">{L.scheduleSubAdmin}</p> : null}
          <div className="cup-scores-team-filter">
            <label className="cup-scores-team-filter__label" htmlFor="cup-scores-team-filter-input">
              {L.scheduleTeamFilterLabel}
            </label>
            <div className="cup-scores-team-filter__row">
              <input
                id="cup-scores-team-filter-input"
                type="text"
                role="combobox"
                aria-haspopup="listbox"
                className="cup-scores-team-filter__input"
                value={teamFilterQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setTeamFilterQuery(v);
                  if (!v.trim()) {
                    setTeamFilterId(null);
                    return;
                  }
                  if (teamFilterId) {
                    const selected = teamOptions.find((o) => o.id === teamFilterId);
                    if (selected && v !== selected.label) setTeamFilterId(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && teamFilterSuggestions[0]) {
                    e.preventDefault();
                    selectTeamFilter(teamFilterSuggestions[0].id);
                  }
                  if (e.key === 'Escape') clearTeamFilter();
                }}
                placeholder={L.scheduleTeamFilterPlaceholder}
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls="cup-scores-team-filter-suggestions"
                aria-expanded={teamFilterSuggestions.length > 0 && !teamFilterId}
              />
              {teamFilterId ? (
                <button type="button" className="cup-scores-team-filter__clear" onClick={clearTeamFilter}>
                  {L.scheduleTeamFilterClear}
                </button>
              ) : null}
            </div>
            {!teamFilterId && teamFilterQuery.trim() && teamFilterSuggestions.length > 0 ? (
              <ul
                id="cup-scores-team-filter-suggestions"
                className="cup-scores-team-filter__suggestions"
                role="listbox"
              >
                {teamFilterSuggestions.map((o) => (
                  <li key={o.id} role="option" aria-selected={teamFilterId === o.id}>
                    <button type="button" onClick={() => selectTeamFilter(o.id)}>
                      <span className="cup-scores-team-filter__name">{o.label}</span>
                      <span className="cup-scores-team-filter__slot">{o.id}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {teamFilterLabel ? (
              <p className="cup-scores-team-filter__active">{L.scheduleTeamFilterActive(teamFilterLabel)}</p>
            ) : null}
          </div>
          <div className="cup-scores-schedule">
            {teamFilterId && filteredSlots.length === 0 ? (
              <p className="cup-scores-muted cup-scores-team-filter__empty">{L.scheduleTeamFilterEmpty}</p>
            ) : null}
            {filteredSlots.map(({ slot, items, ko }) => {
              const matchCount = items.length + ko.length;
              if (matchCount === 0) return null;
              return (
                <div key={slot} className="cup-scores-slot">
                  <div className="cup-scores-slot__time">
                    <span className="cup-scores-slot__time-badge">
                      <strong>{slotKickoffLabel(gen, slot)}</strong>
                      <span className="cup-scores-slot__time-end">– {slotEndLabel(gen, slot)}</span>
                    </span>
                  </div>
                  <div
                    className={
                      matchCount >= 4
                        ? 'cup-scores-slot__grid cup-scores-slot__grid--quad'
                        : 'cup-scores-slot__grid'
                    }
                  >
                    {items.map((m) => (
                      <div
                        key={m.id}
                        className={`cup-scores-match${
                          teamFilterId && matchInvolvesTeam(m.home, m.away, teamFilterId)
                            ? ' cup-scores-match--team-focus'
                            : ''
                        }`}
                      >
                        <span className="cup-scores-match__meta">
                          <span className="cup-scores-match__pill">
                            {L.pitch} {physicalPitch(gen, m.lane, slot)}
                          </span>
                          <span className="cup-scores-match__dot" aria-hidden>
                            ·
                          </span>
                          <span>
                            {L.groupPrefix} {m.group}
                          </span>
                        </span>
                        <div
                          className="cup-scores-match__scoreboard"
                          role="group"
                          aria-label={`${formatTeam(m.home)} v ${formatTeam(m.away)}`}
                        >
                          <span className="cup-scores-match__team cup-scores-match__team--home">
                            {formatTeam(m.home)}
                          </span>
                          <div className="cup-scores-match__inputs">
                            <ScoreInput
                              value={results[resultKey(m.id)]?.home}
                              onCommit={(v) => commitScore(m.id, 'home', v)}
                              ariaLabel={`${formatTeam(m.home)} ${L.scoreHint}`}
                              disabled={!editable}
                            />
                            <span className="cup-scores-match__colon" aria-hidden>
                              :
                            </span>
                            <ScoreInput
                              value={results[resultKey(m.id)]?.away}
                              onCommit={(v) => commitScore(m.id, 'away', v)}
                              ariaLabel={`${formatTeam(m.away)} ${L.scoreHint}`}
                              disabled={!editable}
                            />
                          </div>
                          <span className="cup-scores-match__team cup-scores-match__team--away">
                            {formatTeam(m.away)}
                          </span>
                        </div>
                      </div>
                    ))}
                    {ko.map((k) => {
                      const koPair = getKoMatchPair(k, effectiveBracket);
                      const koSc = results[resultKey(k.id)];
                      const isDraw =
                        koSc?.home != null &&
                        koSc?.away != null &&
                        !Number.isNaN(koSc.home) &&
                        !Number.isNaN(koSc.away) &&
                        koSc.home === koSc.away;
                      const koLabel = koRowLabel(
                        k.labelKey,
                        effectiveBracket,
                        L,
                        gen,
                        results,
                        teamNames,
                        schematicCtx,
                        poScheme
                      );
                      return (
                      <div
                        key={k.id}
                        className={`cup-scores-match cup-scores-match--ko${
                          teamFilterId &&
                          koPair &&
                          matchInvolvesTeam(koPair.home, koPair.away, teamFilterId)
                            ? ' cup-scores-match--team-focus'
                            : ''
                        }`}
                      >
                        <span className="cup-scores-match__meta">
                          <span className="cup-scores-match__pill cup-scores-match__pill--ko">
                            {L.pitch} {physicalPitch(gen, k.lane, slot)}
                          </span>
                        </span>
                        <div className="cup-scores-match__ko-row">
                          <span className="cup-scores-match__ko-label">{koLabel}</span>
                          {koPair?.home && koPair?.away ? (
                            <div
                              className="cup-scores-match__scoreboard cup-scores-match__scoreboard--ko"
                              role="group"
                              aria-label={koLabel}
                            >
                              <span className="cup-scores-match__team cup-scores-match__team--home">
                                {formatTeam(koPair.home)}
                              </span>
                              <div className="cup-scores-match__inputs cup-scores-match__inputs--ko">
                                <ScoreInput
                                  value={koSc?.home}
                                  onCommit={(v) => commitScore(k.id, 'home', v)}
                                  ariaLabel={`${formatTeam(koPair.home)} ${L.scoreHint}`}
                                  disabled={!editable}
                                />
                                <span className="cup-scores-match__colon" aria-hidden>
                                  :
                                </span>
                                <ScoreInput
                                  value={koSc?.away}
                                  onCommit={(v) => commitScore(k.id, 'away', v)}
                                  ariaLabel={`${formatTeam(koPair.away)} ${L.scoreHint}`}
                                  disabled={!editable}
                                />
                              </div>
                              <span className="cup-scores-match__team cup-scores-match__team--away">
                                {formatTeam(koPair.away)}
                              </span>
                            </div>
                          ) : (
                            <div className="cup-scores-match__inputs cup-scores-match__inputs--ko">
                              <ScoreInput
                                value={koSc?.home}
                                onCommit={(v) => commitScore(k.id, 'home', v)}
                                ariaLabel={`${koLabel} home`}
                                disabled={!editable}
                              />
                              <span className="cup-scores-match__colon" aria-hidden>
                                :
                              </span>
                              <ScoreInput
                                value={koSc?.away}
                                onCommit={(v) => commitScore(k.id, 'away', v)}
                                ariaLabel={`${koLabel} away`}
                                disabled={!editable}
                              />
                            </div>
                          )}
                        </div>
                        {isDraw ? (
                          <div className="cup-scores-match__penalties" role="group" aria-label={L.koPenaltiesLabel}>
                            <span className="cup-scores-match__penalties-label">{L.koPenaltiesLabel}</span>
                            <div className="cup-scores-match__inputs cup-scores-match__inputs--ko">
                              <ScoreInput
                                value={koSc?.penHome}
                                onCommit={(v) => commitPenScore(k.id, 'penHome', v)}
                                ariaLabel={`${koPair?.home ? formatTeam(koPair.home) : 'home'} ${L.koPenaltiesHint}`}
                                disabled={!editable}
                              />
                              <span className="cup-scores-match__colon" aria-hidden>
                                :
                              </span>
                              <ScoreInput
                                value={koSc?.penAway}
                                onCommit={(v) => commitPenScore(k.id, 'penAway', v)}
                                ariaLabel={`${koPair?.away ? formatTeam(koPair.away) : 'away'} ${L.koPenaltiesHint}`}
                                disabled={!editable}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                    })}
                  </div>
                </div>
              );
            })}
            {!teamFilterId ? (
            <div className="cup-scores-slot cup-scores-slot--medal" aria-label={L.medalCeremonyTitle}>
              <div className="cup-scores-slot__time">
                <span className="cup-scores-slot__time-badge cup-scores-slot__time-badge--medal">
                  <strong>
                    {gen === 2014
                      ? `${medalCeremonyStartClock(gen)}–${medalCeremonyApproxEndClock2014()}`
                      : medalCeremonyStartClock(gen)}
                  </strong>
                </span>
              </div>
              <div className="cup-scores-slot__grid cup-scores-slot__grid--medal">
                <div className="cup-scores-medal-block">
                  <p className="cup-scores-medal-block__title">{L.medalCeremonyTitle}</p>
                  <p className="cup-scores-muted cup-scores-medal-block__text">{L.medalCeremonyLead}</p>
                  {gen === 2014 ? (
                    <p className="cup-scores-muted cup-scores-medal-block__text">{L.medalCeremonyNote2014}</p>
                  ) : null}
                </div>
              </div>
            </div>
            ) : null}
          </div>
        </section>

        <section
          id="cup-scores-anchor-tables"
          className="cup-scores-card cup-scores-card--standings cup-scores-anchor-target"
          aria-labelledby="stand-h"
        >
          <div className="cup-scores-card__head">
            <h2 id="stand-h">{L.standingsTitle}</h2>
          </div>
          <p className="cup-scores-muted">
            {editable ? L.standingsSubAdmin : standingsSubtitle(L, format)}
          </p>
          <div className="cup-scores-standings-grid">
            {activeGroups.map((g) => (
              <div
                key={g}
                id={`cup-scores-anchor-group-${g.toLowerCase()}`}
                className="cup-scores-group-block cup-scores-anchor-target"
              >
                <h3>
                  {L.groupPrefix} {g}
                </h3>
                <StandingsTable
                  rows={buildStandings(g, results, gen)}
                  L={L}
                  knockout={format.knockout}
                  gen={gen}
                  teamNames={teamNames}
                  editable={editable}
                  onTeamNameCommit={commitTeamName}
                  formatTeam={formatTeam}
                />
              </div>
            ))}
          </div>
        </section>

        {format.knockout === 'quarter_finals' ? (
        <section
          id="cup-scores-anchor-thirds"
          className="cup-scores-card cup-scores-anchor-target"
          aria-labelledby="thirds-h"
        >
          <div className="cup-scores-card__head">
            <h2 id="thirds-h">{L.thirdsTitle}</h2>
          </div>
          <p className="cup-scores-muted">{thirdsSubtitle(L, format, gen)}</p>
          <ThirdsRankTable
            rows={thirdsList}
            L={L}
            qualifyCount={format.bestThirdsToKo}
            formatTeam={formatTeam}
            groupStageComplete={groupStageComplete}
          />
        </section>
        ) : null}

        <section
          id="cup-scores-anchor-bracket"
          className="cup-scores-card cup-scores-card--bracket cup-scores-anchor-target"
          aria-labelledby="bracket-h"
        >
          <div className="cup-scores-card__head">
            <h2 id="bracket-h">
              {format.knockout === 'league_table' ? L.navPlacements : L.bracketTitle}
            </h2>
          </div>
          {editable && format.knockout !== 'league_table' && format.knockout !== 'league_playoffs' ? (
            <details className="cup-scores-ko-manual">
              <summary className="cup-scores-ko-manual__summary">{L.koManualSectionTitle}</summary>
              <p className="cup-scores-muted cup-scores-ko-manual__lead">{L.koManualSectionLead}</p>
              <p className="cup-scores-muted cup-scores-ko-manual__hint">{L.koManualIncompleteHint}</p>
              <div className="cup-scores-ko-manual__actions">
                <button
                  type="button"
                  className="cup-scores-linkbtn"
                  onClick={() => {
                    const c = computeKnockoutBracket(results, gen);
                    if (format.knockout === 'quarter_finals' && c?.format === 'quarter_finals') {
                      setKoBracketDraft((p) => ({
                        ...p,
                        [String(gen)]: {
                          format: 'quarter_finals',
                          qf: c.qf.map((x) => ({ home: x.home, away: x.away })),
                        },
                      }));
                    } else if (format.knockout === 'semi_direct' && c?.format === 'semi_direct') {
                      setKoBracketDraft((p) => ({
                        ...p,
                        [String(gen)]: {
                          format: 'semi_direct',
                          playoffs: [
                            { home: c.playoffs[0].home, away: c.playoffs[0].away },
                            { home: c.playoffs[1].home, away: c.playoffs[1].away },
                          ],
                          sf: [
                            { home: c.sf[0].home, away: c.sf[0].away },
                            { home: c.sf[1].home, away: c.sf[1].away },
                          ],
                        },
                      }));
                    }
                  }}
                >
                  {L.koManualFillFromAuto}
                </button>
                <button
                  type="button"
                  className="cup-scores-linkbtn"
                  onClick={() => {
                    setKoBracketDraft((p) => {
                      const n = { ...p };
                      delete n[String(gen)];
                      return n;
                    });
                  }}
                >
                  {L.koManualClear}
                </button>
              </div>
              {format.knockout === 'quarter_finals' ? (
                <div className="cup-scores-ko-manual__grid" role="group" aria-label={L.koManualSectionTitle}>
                  {(['qf1', 'qf2', 'qf3', 'qf4'] as const).map((qk, i) => {
                    const row = quarterDraftForGen(koBracketDraft, gen);
                    const p = row.format === 'quarter_finals' ? row.qf[i] : { home: '', away: '' };
                    return (
                      <React.Fragment key={qk}>
                        <span className="cup-scores-ko-manual__letter">{KO_SLOT_LETTERS[i]}</span>
                        <span className="cup-scores-ko-manual__qfname">{L.koLabels[qk]}</span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.home}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualHome} · ${L.koLabels[qk]} (${KO_SLOT_LETTERS[i]})`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = quarterDraftForGen(prev, gen);
                              if (cur.format !== 'quarter_finals') return prev;
                              const qf = [...cur.qf];
                              qf[i] = { ...qf[i]!, home: v };
                              return { ...prev, [String(gen)]: { format: 'quarter_finals', qf } };
                            });
                          }}
                        />
                        <span className="cup-scores-ko-manual__vs" aria-hidden>
                          v
                        </span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.away}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualAway} · ${L.koLabels[qk]} (${KO_SLOT_LETTERS[i]})`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = quarterDraftForGen(prev, gen);
                              if (cur.format !== 'quarter_finals') return prev;
                              const qf = [...cur.qf];
                              qf[i] = { ...qf[i]!, away: v };
                              return { ...prev, [String(gen)]: { format: 'quarter_finals', qf } };
                            });
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className="cup-scores-ko-manual__grid cup-scores-ko-manual__grid--semi" role="group" aria-label={L.koManualSectionTitle}>
                  {([0, 1] as const).map((i) => {
                    const letter = KO_SLOT_LETTERS[i];
                    const row = semiDraftForGen(koBracketDraft, gen);
                    const p = row.format === 'semi_direct' ? row.playoffs[i] : { home: '', away: '' };
                    return (
                      <React.Fragment key={`po-${i}`}>
                        <span className="cup-scores-ko-manual__letter">{letter}</span>
                        <span className="cup-scores-ko-manual__qfname">{L.koManualPlayoffPair(letter)}</span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.home}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualHome} · ${L.koManualPlayoffPair(letter)}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = semiDraftForGen(prev, gen);
                              if (cur.format !== 'semi_direct') return prev;
                              const playoffs: [BracketPair, BracketPair] = [
                                { ...cur.playoffs[0] },
                                { ...cur.playoffs[1] },
                              ];
                              playoffs[i] = { ...playoffs[i], home: v };
                              return {
                                ...prev,
                                [String(gen)]: { format: 'semi_direct', playoffs, sf: [...cur.sf] },
                              };
                            });
                          }}
                        />
                        <span className="cup-scores-ko-manual__vs" aria-hidden>
                          v
                        </span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.away}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualAway} · ${L.koManualPlayoffPair(letter)}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = semiDraftForGen(prev, gen);
                              if (cur.format !== 'semi_direct') return prev;
                              const playoffs: [BracketPair, BracketPair] = [
                                { ...cur.playoffs[0] },
                                { ...cur.playoffs[1] },
                              ];
                              playoffs[i] = { ...playoffs[i], away: v };
                              return {
                                ...prev,
                                [String(gen)]: { format: 'semi_direct', playoffs, sf: [...cur.sf] },
                              };
                            });
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                  {([0, 1] as const).map((i) => {
                    const row = semiDraftForGen(koBracketDraft, gen);
                    const p = row.format === 'semi_direct' ? row.sf[i] : { home: '', away: '' };
                    const n = (i + 1) as 1 | 2;
                    return (
                      <React.Fragment key={`sf-${i}`}>
                        <span className="cup-scores-ko-manual__letter cup-scores-ko-manual__letter--wide">SF{n}</span>
                        <span className="cup-scores-ko-manual__qfname">{L.koManualSfPair(n)}</span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.home}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualHome} · ${L.koManualSfPair(n)}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = semiDraftForGen(prev, gen);
                              if (cur.format !== 'semi_direct') return prev;
                              const sf: [BracketPair, BracketPair] = [{ ...cur.sf[0] }, { ...cur.sf[1] }];
                              sf[i] = { ...sf[i], home: v };
                              return {
                                ...prev,
                                [String(gen)]: { format: 'semi_direct', playoffs: [...cur.playoffs], sf },
                              };
                            });
                          }}
                        />
                        <span className="cup-scores-ko-manual__vs" aria-hidden>
                          v
                        </span>
                        <input
                          className="cup-scores-ko-manual__input"
                          value={p.away}
                          placeholder={L.koManualTeamHint}
                          aria-label={`${L.koManualAway} · ${L.koManualSfPair(n)}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKoBracketDraft((prev) => {
                              const cur = semiDraftForGen(prev, gen);
                              if (cur.format !== 'semi_direct') return prev;
                              const sf: [BracketPair, BracketPair] = [{ ...cur.sf[0] }, { ...cur.sf[1] }];
                              sf[i] = { ...sf[i], away: v };
                              return {
                                ...prev,
                                [String(gen)]: { format: 'semi_direct', playoffs: [...cur.playoffs], sf },
                              };
                            });
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </details>
          ) : null}
          {format.knockout === 'league_table' ? (
            !effectiveBracket || effectiveBracket.format !== 'league_table' ? (
              <p className="cup-scores-muted">{L.bracketLeagueTableSub}</p>
            ) : (
              <ol className="cup-scores-placements">
                <li>
                  <span className="cup-scores-placements__label">{L.placementChampion}</span>
                  <span className="cup-scores-placements__team">{formatTeam(effectiveBracket.champion)}</span>
                </li>
                <li>
                  <span className="cup-scores-placements__label">{L.placementRunnerUp}</span>
                  <span className="cup-scores-placements__team">{formatTeam(effectiveBracket.runnerUp)}</span>
                </li>
                <li>
                  <span className="cup-scores-placements__label">{L.placementThird}</span>
                  <span className="cup-scores-placements__team">{formatTeam(effectiveBracket.thirdPlace)}</span>
                </li>
              </ol>
            )
          ) : format.knockout === 'league_playoffs' ? (
            <>
              <p className="cup-scores-muted">{L.bracketLeaguePlayoffsSub}</p>
              {effectiveBracket?.format === 'league_playoffs' ? (
                <>
                  <div className="cup-scores-bracket__body">
                    <div className="cup-scores-bracket__block">
                      <h3 className="cup-scores-bracket__subh">{L.koLabels.final}</h3>
                      <ul className="cup-scores-bracket__qf">
                        <li>
                          <span className="cup-scores-bracket__qf-pair">
                            {formatPairVs(effectiveBracket.final.home, effectiveBracket.final.away)}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="cup-scores-bracket__block">
                      <h3 className="cup-scores-bracket__subh">{L.koLabels.third}</h3>
                      <ul className="cup-scores-bracket__qf">
                        <li>
                          <span className="cup-scores-bracket__qf-pair">
                            {formatPairVs(
                              effectiveBracket.thirdPlaceMatch.home,
                              effectiveBracket.thirdPlaceMatch.away
                            )}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {koMedalists.champion || koMedalists.runnerUp || koMedalists.thirdPlace ? (
                    <KnockoutPlacements medals={koMedalists} L={L} formatTeam={formatTeam} />
                  ) : null}
                </>
              ) : null}
            </>
          ) : format.knockout === 'semi_direct' ? (
            !effectiveBracket || effectiveBracket.format !== 'semi_direct' ? (
              <div className="cup-scores-bracket__block cup-scores-bracket__block--preview">
                <h3 className="cup-scores-bracket__subh">{L.bracketPlayoffTitle}</h3>
                <ul className="cup-scores-bracket__qf">
                  {(['po1', 'po2'] as const).map((key, i) => (
                    <li key={key}>
                      <span className="cup-scores-bracket__qf-letter">{KO_SLOT_LETTERS[i]}</span>
                      <span className="cup-scores-bracket__qf-pair">
                        {formatSchematicPair(
                          poScheme[key][0],
                          poScheme[key][1],
                          schematicCtx,
                          ' v '
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <ul className="cup-scores-bracket__later">
                  <li>
                    {L.koLabels.sf1} · {resolveSchematicTeam('B1', schematicCtx)} vs{' '}
                    {roleLabels.rolePlayoffWinner('E')} (
                    {formatSchematicPair(poScheme.po1[0], poScheme.po1[1], schematicCtx, '–')}
                    )
                  </li>
                  <li>
                    {L.koLabels.sf2} · {resolveSchematicTeam('A1', schematicCtx)} vs{' '}
                    {roleLabels.rolePlayoffWinner('F')} (
                    {formatSchematicPair(poScheme.po2[0], poScheme.po2[1], schematicCtx, '–')}
                    )
                  </li>
                  <li>{L.koLabels.final}</li>
                  <li>{L.koLabels.third}</li>
                </ul>
              </div>
            ) : (
              <div className="cup-scores-bracket__body">
                <div className="cup-scores-bracket__block">
                  <h3 className="cup-scores-bracket__subh">{L.bracketPlayoffTitle}</h3>
                  <ul className="cup-scores-bracket__qf">
                    {effectiveBracket.playoffs.map((p, i) => (
                      <li key={`po-${i}`}>
                        <span className="cup-scores-bracket__qf-letter">{KO_SLOT_LETTERS[i]}</span>
                        <span className="cup-scores-bracket__qf-pair">
                          {formatPairVs(p.home, p.away)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <ul className="cup-scores-bracket__later">
                  <li>
                    {effectiveBracket.sf[0].home && effectiveBracket.sf[0].away
                      ? `${L.koLabels.sf1} · ${formatPairVs(effectiveBracket.sf[0].home, effectiveBracket.sf[0].away)}`
                      : `${L.koLabels.sf1} · ${resolveSchematicTeam('B1', schematicCtx)} vs ${
                          po1Winner ? formatTeam(po1Winner) : roleLabels.rolePlayoffWinner('E')
                        }`}
                  </li>
                  <li>
                    {effectiveBracket.sf[1].home && effectiveBracket.sf[1].away
                      ? `${L.koLabels.sf2} · ${formatPairVs(effectiveBracket.sf[1].home, effectiveBracket.sf[1].away)}`
                      : `${L.koLabels.sf2} · ${resolveSchematicTeam('A1', schematicCtx)} vs ${
                          po2Winner ? formatTeam(po2Winner) : roleLabels.rolePlayoffWinner('F')
                        }`}
                  </li>
                  <li>
                    {effectiveBracket.final.home && effectiveBracket.final.away
                      ? `${L.koLabels.final} · ${formatPairVs(effectiveBracket.final.home, effectiveBracket.final.away)}`
                      : L.koLabels.final}
                  </li>
                  <li>
                    {effectiveBracket.thirdPlaceMatch.home && effectiveBracket.thirdPlaceMatch.away
                      ? `${L.koLabels.third} · ${formatPairVs(
                          effectiveBracket.thirdPlaceMatch.home,
                          effectiveBracket.thirdPlaceMatch.away
                        )}`
                      : L.koLabels.third}
                  </li>
                </ul>
                <KnockoutPlacements medals={koMedalists} L={L} formatTeam={formatTeam} />
              </div>
            )
          ) : !effectiveBracket || effectiveBracket.format !== 'quarter_finals' ? (
            <div className="cup-scores-bracket__block cup-scores-bracket__block--preview">
              <h3 className="cup-scores-bracket__subh">{L.bracketQfTitle}</h3>
              <ul className="cup-scores-bracket__qf">
                {(['qf1', 'qf2', 'qf3', 'qf4'] as const).map((key, i) => (
                  <li key={key}>
                    <span className="cup-scores-bracket__qf-letter">{KO_SLOT_LETTERS[i]}</span>
                    <span className="cup-scores-bracket__qf-pair">
                      {formatSchematicPair(
                        QF_SCHEMATIC[key][0],
                        QF_SCHEMATIC[key][1],
                        schematicCtx,
                        ' v '
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <ul className="cup-scores-bracket__later">
                <li>{L.bracketSemi1Pair}</li>
                <li>{L.bracketSemi2Pair}</li>
                <li>{L.koLabels.final}</li>
                <li>{L.koLabels.third}</li>
              </ul>
            </div>
          ) : (
            <div className="cup-scores-bracket__body">
              <div className="cup-scores-bracket__block">
                <h3 className="cup-scores-bracket__subh">{L.bracketQfTitle}</h3>
                <ul className="cup-scores-bracket__qf">
                  {effectiveBracket.qf.map((p, i) => (
                    <li key={`qf-${i}`}>
                      <span className="cup-scores-bracket__qf-letter">{KO_SLOT_LETTERS[i]}</span>
                      <span className="cup-scores-bracket__qf-pair">
                        {formatPairVs(p.home, p.away)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <ul className="cup-scores-bracket__later">
                <li>
                  {effectiveBracket.sf[0].home && effectiveBracket.sf[0].away
                    ? `${L.koLabels.sf1} · ${formatPairVs(effectiveBracket.sf[0].home, effectiveBracket.sf[0].away)}`
                    : L.bracketSemi1Pair}
                </li>
                <li>
                  {effectiveBracket.sf[1].home && effectiveBracket.sf[1].away
                    ? `${L.koLabels.sf2} · ${formatPairVs(effectiveBracket.sf[1].home, effectiveBracket.sf[1].away)}`
                    : L.bracketSemi2Pair}
                </li>
                <li>
                  {effectiveBracket.final.home && effectiveBracket.final.away
                    ? `${L.koLabels.final} · ${formatPairVs(effectiveBracket.final.home, effectiveBracket.final.away)}`
                    : L.koLabels.final}
                </li>
                <li>
                  {effectiveBracket.thirdPlaceMatch.home && effectiveBracket.thirdPlaceMatch.away
                    ? `${L.koLabels.third} · ${formatPairVs(
                        effectiveBracket.thirdPlaceMatch.home,
                        effectiveBracket.thirdPlaceMatch.away
                      )}`
                    : L.koLabels.third}
                </li>
              </ul>
              <KnockoutPlacements medals={koMedalists} L={L} formatTeam={formatTeam} />
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

function KnockoutPlacements({
  medals,
  L,
  formatTeam,
}: {
  medals: KnockoutMedalists;
  L: ReturnType<typeof getCupScoresLocale>;
  formatTeam: (teamId: string) => string;
}) {
  if (!medals.champion && !medals.runnerUp && !medals.thirdPlace) return null;
  return (
    <ol className="cup-scores-placements">
      {medals.champion ? (
        <li>
          <span className="cup-scores-placements__label">{L.placementChampion}</span>
          <span className="cup-scores-placements__team">{formatTeam(medals.champion)}</span>
        </li>
      ) : null}
      {medals.runnerUp ? (
        <li>
          <span className="cup-scores-placements__label">{L.placementRunnerUp}</span>
          <span className="cup-scores-placements__team">{formatTeam(medals.runnerUp)}</span>
        </li>
      ) : null}
      {medals.thirdPlace ? (
        <li>
          <span className="cup-scores-placements__label">{L.placementThird}</span>
          <span className="cup-scores-placements__team">{formatTeam(medals.thirdPlace)}</span>
        </li>
      ) : null}
    </ol>
  );
}

function ScoreInput({
  value,
  onCommit,
  ariaLabel,
  disabled,
}: {
  value: number | null | undefined;
  onCommit: (v: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const committed = value === null || value === undefined ? '' : String(value);
  const [draft, setDraft] = useState(committed);
  const focusedRef = React.useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(committed);
    }
  }, [committed]);

  const commit = useCallback(() => {
    const normalized = draft.replace(/\D/g, '');
    if (normalized === committed) return;
    onCommit(normalized);
  }, [committed, draft, onCommit]);

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={2}
      className="cup-scores-input"
      aria-label={ariaLabel}
      disabled={disabled}
      value={draft}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function standingsRowClass(idx: number, knockout: KnockoutFormat): string {
  if (knockout === 'league_table') {
    if (idx === 0) return 'cup-scores-tr cup-scores-tr--adv1';
    if (idx === 2) return 'cup-scores-tr cup-scores-tr--adv2';
    return 'cup-scores-tr';
  }
  if (knockout === 'league_playoffs') {
    if (idx === 0) return 'cup-scores-tr cup-scores-tr--adv1';
    if (idx === 1) return 'cup-scores-tr cup-scores-tr--adv2';
    if (idx === 2 || idx === 3) return 'cup-scores-tr cup-scores-tr--adv2';
    return 'cup-scores-tr';
  }
  if (knockout === 'semi_direct') {
    if (idx === 0) return 'cup-scores-tr cup-scores-tr--adv1';
    if (idx === 1 || idx === 2) return 'cup-scores-tr cup-scores-tr--adv2';
    return 'cup-scores-tr';
  }
  if (idx === 0) return 'cup-scores-tr cup-scores-tr--adv1';
  if (idx === 1) return 'cup-scores-tr cup-scores-tr--adv2';
  return 'cup-scores-tr';
}

function TeamNameInput({
  teamId,
  committed,
  placeholder,
  ariaLabel,
  onCommit,
}: {
  teamId: string;
  committed: string;
  placeholder: string;
  ariaLabel: string;
  onCommit: (teamId: string, value: string) => void;
}) {
  const [draft, setDraft] = useState(committed);

  useEffect(() => {
    setDraft(committed);
  }, [committed, teamId]);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    const normalized = trimmed || '';
    if (normalized === (committed || '')) return;
    onCommit(teamId, normalized);
  }, [committed, draft, onCommit, teamId]);

  return (
    <input
      type="text"
      className="cup-scores-team-input"
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function ThirdsRankTable({
  rows,
  L,
  qualifyCount,
  formatTeam,
  groupStageComplete,
}: {
  rows: ReturnType<typeof thirdPlacedTeamsProvisional>;
  L: ReturnType<typeof getCupScoresLocale>;
  qualifyCount: number;
  formatTeam: (teamId: string) => string;
  groupStageComplete: boolean;
}) {
  return (
    <div className="cup-scores-table-wrap cup-scores-table-wrap--thirds">
      <table className="cup-scores-table cup-scores-table--thirds">
        <thead>
          <tr>
            <th className="cup-scores-table__rank-col">{L.thRank}</th>
            <th>{L.thGroup}</th>
            <th>{L.thTeam}</th>
            <th>{L.thP}</th>
            <th>{L.thW}</th>
            <th>{L.thD}</th>
            <th>{L.thL}</th>
            <th>{L.thGF}</th>
            <th>{L.thGA}</th>
            <th>{L.thGD}</th>
            <th>{L.thPts}</th>
            <th className="cup-scores-table__status-col">{L.thStatus}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => {
            const qualified = groupStageComplete && i < qualifyCount;
            const showOut =
              groupStageComplete && rows.length > qualifyCount && i === qualifyCount;
            return (
              <tr
                key={`${t.group}-${t.team}`}
                className={qualified ? 'cup-scores-tr cup-scores-tr--adv1' : 'cup-scores-tr'}
              >
                <td className="cup-scores-table__rank-col">{i + 1}</td>
                <td>{t.group}</td>
                <td>
                  <span className="cup-scores-tr__team">{formatTeam(t.team)}</span>
                </td>
                <td>{t.row.p}</td>
                <td>{t.row.w}</td>
                <td>{t.row.d}</td>
                <td>{t.row.l}</td>
                <td>{t.row.gf}</td>
                <td>{t.row.ga}</td>
                <td>{t.row.gd > 0 ? `+${t.row.gd}` : t.row.gd}</td>
                <td>
                  <span className="cup-scores-tr__pts">{t.row.pts}</span>
                </td>
                <td className="cup-scores-table__status-col">
                  {qualified ? (
                    <span className="cup-scores-thirds-badge">{L.thirdQualified}</span>
                  ) : showOut ? (
                    <span className="cup-scores-thirds-badge cup-scores-thirds-badge--out">
                      {L.thirdNotQualified}
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StandingsTable({
  rows,
  L,
  knockout,
  gen,
  teamNames,
  editable,
  onTeamNameCommit,
  formatTeam,
}: {
  rows: ReturnType<typeof buildStandings>;
  L: ReturnType<typeof getCupScoresLocale>;
  knockout: KnockoutFormat;
  gen: GenerationYear;
  teamNames: TeamNamesMap;
  editable: boolean;
  onTeamNameCommit: (teamId: string, value: string) => void;
  formatTeam: (teamId: string) => string;
}) {
  return (
    <div className="cup-scores-table-wrap">
      <table className="cup-scores-table">
        <thead>
          <tr>
            <th>{L.thTeam}</th>
            <th>{L.thP}</th>
            <th>{L.thW}</th>
            <th>{L.thD}</th>
            <th>{L.thL}</th>
            <th>{L.thGF}</th>
            <th>{L.thGA}</th>
            <th>{L.thGD}</th>
            <th>{L.thPts}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r.team} className={standingsRowClass(idx, knockout)}>
              <td>
                {editable ? (
                  <div className="cup-scores-team-cell">
                    <TeamNameInput
                      teamId={r.team}
                      committed={teamNames[teamStorageKey(gen, r.team)] ?? ''}
                      placeholder={r.team}
                      ariaLabel={`${L.teamNamePlaceholder} ${r.team}`}
                      onCommit={onTeamNameCommit}
                    />
                    <span className="cup-scores-team-cell__slot">{r.team}</span>
                  </div>
                ) : (
                  <span className="cup-scores-tr__team">{formatTeam(r.team)}</span>
                )}
              </td>
              <td>{r.p}</td>
              <td>{r.w}</td>
              <td>{r.d}</td>
              <td>{r.l}</td>
              <td>{r.gf}</td>
              <td>{r.ga}</td>
              <td>{r.gd > 0 ? `+${r.gd}` : r.gd}</td>
              <td>
                <span className="cup-scores-tr__pts">{r.pts}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CupScores;
